"use client";

import { AnimatePresence, motion } from "motion/react";
import {
  CalendarClock,
  CalendarDays as CalendarIcon,
  Clock,
  Send,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "../../../shadcn/ui/select";
import {
  formatDateShort,
  formatFull,
  formatTime12,
  generateTimeSlots,
  groupTimeSlots,
  parseTimeValue,
  toTimeValue,
} from "./scheduler-utils";
import { useEffect, useRef } from "react";

import { Calendar } from "../../../shadcn/ui/calendar";
import type { Preset } from "./scheduler-utils";
import { PunchupButton } from "../../common/PunchupButton";
import { TimeSlotSelectContent } from "./TimeSlotSelectContent";
import dayjs from "@punchup/utils/dayjs";
import { useSchedulerState } from "./useSchedulerState";

export interface MobileFooterConfig {
  onClick: () => void;
  disabled: boolean;
  isImmediate: boolean;
  isSubmitting: boolean;
}

export interface SmartSchedulerProps {
  scheduledDate: string | null;
  onSchedule: (isoDate: string | null) => void;
  isSubmitting: boolean;
  isReviewReady: boolean;
  isMobile: boolean;
  onMobileFooterConfig?: (config: MobileFooterConfig) => void;
  /**
   * When true, suppress the built-in confirmation bar + submit button and
   * instead sync `onSchedule` reactively on every state change. Intended for
   * embedding the picker UI inside a host form that owns its own submit flow.
   */
  pickerOnly?: boolean;
  /**
   * When false, hide the quick-pick preset list and start the picker in custom
   * mode. Defaults to true so existing call sites are unaffected.
   */
  showQuickPicks?: boolean;
}

export function SmartScheduler({
  scheduledDate,
  onSchedule,
  isSubmitting,
  isReviewReady,
  isMobile,
  onMobileFooterConfig,
  pickerOnly = false,
  showQuickPicks = true,
}: SmartSchedulerProps) {
  const state = useSchedulerState({
    initialScheduledDate: scheduledDate,
    quickPicksEnabled: showQuickPicks,
  });

  const isToday = state.selectedDate.isSame(state.now, "day");
  const allSlots = generateTimeSlots(isToday, state.now);
  const grouped = groupTimeSlots(allSlots);

  const scheduled = isMobile
    ? state.getScheduledDayjs()
    : (state.getScheduledDayjs() ??
      state.selectedDate
        .hour(state.selectedHour)
        .minute(state.selectedMin)
        .second(0));

  // Track when time select closes to guard against portal click-through.
  // When the Select closes, the portal unmounts mid-event-cycle, allowing
  // the pointerup/click to propagate to the calendar underneath.
  const timeSelectClosedAtRef = useRef(dayjs(0));
  const handleTimeSelectOpenChange = (open: boolean) => {
    if (!open) {
      timeSelectClosedAtRef.current = dayjs();
    }
  };

  const currentTimeValue = toTimeValue(state.selectedHour, state.selectedMin);

  const handleTimeChange = (value: string) => {
    const { h, m } = parseTimeValue(value);
    state.selectTime(h, m);
  };

  const handleSchedule = () => {
    if (state.isImmediateSelected) {
      onSchedule(null);
      return;
    }
    if (!scheduled) return;
    onSchedule(scheduled.toISOString());
  };

  // Stable ref so the footer config callback doesn't cause re-render loops
  const handleScheduleRef = useRef(handleSchedule);
  handleScheduleRef.current = handleSchedule;

  // Mobile: scroll custom controls into view when expanded
  const isCustomActive = state.mode === "custom";
  const customControlsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isMobile && isCustomActive && customControlsRef.current) {
      customControlsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [isMobile, isCustomActive]);

  // Report button config to parent so it can render in the sticky footer.
  // Skipped in pickerOnly mode — host form owns the submit button.
  useEffect(() => {
    if (pickerOnly) return;
    if (isMobile && onMobileFooterConfig) {
      onMobileFooterConfig({
        onClick: () => handleScheduleRef.current(),
        disabled: !isReviewReady || isSubmitting,
        isImmediate: state.isImmediateSelected,
        isSubmitting,
      });
    }
  }, [
    pickerOnly,
    isMobile,
    onMobileFooterConfig,
    isReviewReady,
    isSubmitting,
    state.isImmediateSelected,
  ]);

  // pickerOnly mode: sync selected state → parent on every change, so the host
  // form stays in lockstep with the picker without a submit click.
  const hasSyncedRef = useRef(false);
  useEffect(() => {
    if (!pickerOnly) return;
    // Skip the initial mount pass. Without this, the picker's "next suggested
    // time" default lands in the parent form before the user has chosen
    // anything — and during quiet-hour edge cases (`getNextSuggested` clamps
    // past 9 PM to today 8 AM) that default is in the past, which trips host
    // bounds checks and disables the submit button.
    if (!hasSyncedRef.current) {
      hasSyncedRef.current = true;
      return;
    }
    if (state.isImmediateSelected) {
      onSchedule(null);
      return;
    }
    if (scheduled) {
      onSchedule(scheduled.toISOString());
    }
    // `scheduled` is derived from state.selectedDate/Hour/Min — listing the
    // underlying state fields is more precise than the Dayjs object (which
    // changes identity each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pickerOnly,
    state.isImmediateSelected,
    state.selectedDate,
    state.selectedHour,
    state.selectedMin,
  ]);

  // ─── Shared render helpers ───

  function renderPresetButton(preset: Preset, index: number) {
    const active = isMobile
      ? state.mode === "presets" && state.selectedPresetIndex === index
      : state.isPresetActive(preset);
    const Icon = preset.icon;

    return (
      <button
        key={index}
        type="button"
        onClick={() => state.selectPreset(index)}
        className={`flex w-full items-center text-left transition-all ${
          isMobile
            ? `gap-3 rounded-[14px] border-[1.5px] px-3.5 py-[13px]`
            : `gap-2.5 rounded-[10px] border-[1.5px] px-3 py-[10px]`
        } ${active ? "border-primary bg-primary/5" : "border-zinc-200 bg-white"}`}
      >
        <span
          className={`flex items-center justify-center text-zinc-500 ${isMobile ? "w-7" : "w-5"}`}
        >
          <Icon className={isMobile ? "h-5 w-5" : "h-4 w-4"} />
        </span>
        <div className="flex flex-1 flex-col">
          <span
            className={`font-semibold text-zinc-900 ${isMobile ? "text-sm" : "text-[12px]"}`}
          >
            {preset.label}
          </span>
          <span
            className={`font-medium text-zinc-400 ${isMobile ? "mt-0.5 text-[11.5px]" : "text-[10px]"}`}
          >
            {preset.sub}
          </span>
        </div>
        <div
          className={`flex flex-shrink-0 items-center justify-center rounded-full border-2 transition-all ${
            isMobile ? "h-5 w-5" : "h-4 w-4"
          } ${active ? "border-primary" : "border-zinc-300"}`}
        >
          {active && (
            <div
              className={`rounded-full bg-primary ${isMobile ? "h-2.5 w-2.5" : "h-2 w-2"}`}
            />
          )}
        </div>
      </button>
    );
  }

  function renderCalendar() {
    return (
      <Calendar
        mode="single"
        selected={state.selectedDate.toDate()}
        onSelect={(date) => {
          // Ignore clicks that land right after the time select portal closes
          if (dayjs().diff(timeSelectClosedAtRef.current) < 300) return;
          if (date) state.selectCalendarDate(date);
        }}
        disabled={(date) => dayjs(date).isBefore(state.now, "day")}
        className="rounded-md border"
      />
    );
  }

  function renderTimeSelect() {
    return (
      <Select
        value={currentTimeValue}
        onValueChange={handleTimeChange}
        onOpenChange={handleTimeSelectOpenChange}
      >
        <SelectTrigger className="w-full rounded-[10px] border-[1.5px] border-zinc-200 bg-white px-3 py-2.5 text-[13px] font-semibold text-zinc-900">
          <SelectValue>
            {formatTime12(state.selectedHour, state.selectedMin)}
          </SelectValue>
        </SelectTrigger>
        <SelectContent align="end" className="max-h-[280px]">
          <TimeSlotSelectContent grouped={grouped} />
        </SelectContent>
      </Select>
    );
  }

  // ─── Mobile layout ───

  if (isMobile) {
    return (
      <div className="flex w-full flex-col overflow-hidden rounded-2xl bg-white shadow-sm">
        {/* Option list */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {showQuickPicks ? (
            <div className="flex flex-col gap-1.5">
              {state.presets.map((p, i) => renderPresetButton(p, i))}

              {/* Custom date & time option */}
              <div
                className={`rounded-[14px] border-[1.5px] transition-all ${
                  isCustomActive
                    ? "border-primary bg-primary/5"
                    : "border-zinc-200 bg-white"
                }`}
              >
                <button
                  type="button"
                  onClick={() => state.switchMode("custom")}
                  className="flex w-full items-center gap-3 px-3.5 py-[13px] text-left"
                >
                  <span className="flex w-7 items-center justify-center text-zinc-500">
                    <CalendarClock className="h-5 w-5" />
                  </span>
                  <div className="flex flex-1 flex-col">
                    <span className="text-sm font-semibold text-zinc-900">
                      Custom date & time
                    </span>
                    <span className="mt-0.5 text-[11.5px] font-medium text-zinc-400">
                      Pick a specific date and time
                    </span>
                  </div>
                  <div
                    className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all ${
                      isCustomActive ? "border-primary" : "border-zinc-300"
                    }`}
                  >
                    {isCustomActive && (
                      <div className="h-2.5 w-2.5 rounded-full bg-primary" />
                    )}
                  </div>
                </button>

                {/* Expanded custom controls */}
                {isCustomActive && (
                  <div
                    ref={customControlsRef}
                    className="mx-3.5 mb-3.5 mt-0 rounded-xl border border-zinc-100 bg-zinc-50/50 px-3 py-3"
                  >
                    <div className="mb-2 flex items-center gap-1.5">
                      <CalendarIcon className="h-3.5 w-3.5 text-zinc-400" />
                      <span className="text-[11px] font-bold uppercase tracking-wide text-zinc-400">
                        Select date
                      </span>
                    </div>
                    {renderCalendar()}

                    <div className="mb-2 mt-3.5 flex items-center justify-between">
                      <span className="text-[11px] font-bold uppercase tracking-wide text-zinc-400">
                        Select time
                      </span>
                      <span className="text-[11px] font-medium text-zinc-400">
                        ({state.userTimezone.abbreviation})
                      </span>
                    </div>
                    {renderTimeSelect()}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-zinc-100 bg-zinc-50/50 px-3 py-3">
              <div className="mb-2 flex items-center gap-1.5">
                <CalendarIcon className="h-3.5 w-3.5 text-zinc-400" />
                <span className="text-[11px] font-bold uppercase tracking-wide text-zinc-400">
                  Select date
                </span>
              </div>
              {renderCalendar()}

              <div className="mb-2 mt-3.5 flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-wide text-zinc-400">
                  Select time
                </span>
                <span className="text-[11px] font-medium text-zinc-400">
                  ({state.userTimezone.abbreviation})
                </span>
              </div>
              {renderTimeSelect()}
            </div>
          )}
        </div>

        {/* Schedule summary */}
        <AnimatePresence>
          {(state.isImmediateSelected || scheduled) && (
            <motion.div
              key="schedule-summary"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="overflow-hidden"
            >
              <div className="border-t border-zinc-100 bg-white px-4 pb-3 pt-2.5">
                <motion.div
                  layout
                  className="rounded-[14px] bg-ivory px-3.5 py-3"
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <AnimatePresence mode="wait" initial={false}>
                    {state.isImmediateSelected ? (
                      <motion.div
                        key="immediate"
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                      >
                        <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-gray-600">
                          Sending
                        </span>
                        <span className="mt-0.5 block text-[13px] font-bold text-primary-blue">
                          Immediately
                        </span>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="scheduled"
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                      >
                        <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-gray-600">
                          Sending
                        </span>
                        <span className="mt-0.5 block text-[13px] font-bold text-primary-blue">
                          <AnimatePresence mode="wait" initial={false}>
                            <motion.span
                              key={formatDateShort(scheduled!)}
                              className="inline-block"
                              initial={{ opacity: 0, y: 4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -4 }}
                              transition={{ duration: 0.15, ease: "easeOut" }}
                            >
                              {formatDateShort(scheduled!)}
                            </motion.span>
                          </AnimatePresence>
                          {" at "}
                          <AnimatePresence mode="wait" initial={false}>
                            <motion.span
                              key={formatTime12(
                                scheduled!.hour(),
                                scheduled!.minute()
                              )}
                              className="inline-block"
                              initial={{ opacity: 0, y: 4 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -4 }}
                              transition={{ duration: 0.15, ease: "easeOut" }}
                            >
                              {formatTime12(
                                scheduled!.hour(),
                                scheduled!.minute()
                              )}
                            </motion.span>
                          </AnimatePresence>
                        </span>
                        <span className="mt-0.5 block text-[10px] text-zinc-400">
                          {state.userTimezone.fullName} (
                          {state.userTimezone.abbreviation})
                        </span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // ─── Desktop layout ───

  return (
    <div className="w-full overflow-hidden rounded-2xl bg-white shadow-sm">
      {/* Three-column layout: Quick Picks | Calendar | Time */}
      <div className="flex py-5">
        {showQuickPicks && (
          <>
            {/* Column 1: Quick Picks */}
            <div className="min-w-[210px] flex-1">
              <span className="mb-3 block text-[11px] font-bold uppercase tracking-widest text-zinc-400">
                Quick pick
              </span>
              <div className="flex flex-col gap-1.5">
                {state.presets.map((p, i) => renderPresetButton(p, i))}
              </div>
            </div>

            {/* Divider */}
            <div className="mx-4 w-px self-stretch bg-zinc-200" />
          </>
        )}

        {/* Column 2: Calendar */}
        <div
          className={`flex flex-col transition-opacity ${
            state.isImmediateSelected
              ? "pointer-events-none select-none opacity-40"
              : ""
          }`}
        >
          <div className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-zinc-500">
            <CalendarIcon className="h-3.5 w-3.5" />
            Date
          </div>
          {renderCalendar()}
        </div>

        {/* Divider */}
        <div className="mx-4 w-px self-stretch bg-zinc-200" />

        {/* Column 3: Time dropdown */}
        <div
          className={`w-[160px] flex-shrink-0 transition-opacity ${
            state.isImmediateSelected
              ? "pointer-events-none select-none opacity-40"
              : ""
          }`}
        >
          <div className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-zinc-500">
            <Clock className="h-3.5 w-3.5" />
            <span>
              Time{" "}
              <span className="text-[11px] font-medium normal-case tracking-normal text-zinc-400">
                ({state.userTimezone.abbreviation})
              </span>
            </span>
          </div>
          {renderTimeSelect()}
        </div>
      </div>

      {/* Confirmation bar — hidden in pickerOnly mode (host owns submit) */}
      {pickerOnly ? null : (
        <div className="flex items-center justify-between bg-ivory px-6 py-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-gray-600">
              Your message will be sent
            </div>
            <AnimatePresence mode="wait" initial={false}>
              {state.isImmediateSelected ? (
                <motion.div
                  key="immediate"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                >
                  <div className="mt-0.5 text-[15px] font-bold text-primary-blue">
                    Immediately
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="scheduled"
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                >
                  <div className="mt-0.5 text-[15px] font-bold text-primary-blue">
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.span
                        key={formatDateShort(scheduled!)}
                        className="inline-block"
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                      >
                        {formatDateShort(scheduled!)}
                      </motion.span>
                    </AnimatePresence>
                    {" at "}
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.span
                        key={formatTime12(
                          scheduled!.hour(),
                          scheduled!.minute()
                        )}
                        className="inline-block"
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15, ease: "easeOut" }}
                      >
                        {formatTime12(scheduled!.hour(), scheduled!.minute())}
                      </motion.span>
                    </AnimatePresence>
                  </div>
                  <div className="mt-0.5 text-[11px] text-zinc-400">
                    {state.userTimezone.fullName} (
                    {state.userTimezone.abbreviation})
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <PunchupButton
            onClick={handleSchedule}
            disabled={!isReviewReady || isSubmitting}
            className="gap-1.5"
          >
            {state.isImmediateSelected ? (
              <>
                <Send className="h-3.5 w-3.5" />
                {isSubmitting ? "Sending..." : "Send Message"}
              </>
            ) : (
              <>
                <Clock className="h-3.5 w-3.5" />
                {isSubmitting ? "Scheduling..." : "Schedule Message"}
              </>
            )}
          </PunchupButton>
        </div>
      )}
    </div>
  );
}
